<template>
  <div>
    <div class="booking-finance-actions">
      <app-button ref="previousPayment" @click.native="$modal.show(paymentModalName)">Record Previous Payment</app-button>
    </div>
    <booking-previous-payment-modal :key="bookingId" :name="paymentModalName" :booking="booking" @recorded="loadLedger" @refresh="loadLedger" @closed="focusPaymentButton" />
    <section class="booking-finance" aria-labelledby="booking-invoice-ledger-title" :aria-busy="loading ? 'true' : 'false'">
      <header class="booking-finance__header"><h2 id="booking-invoice-ledger-title">Invoice Ledger</h2></header>
      <app-block-loader v-if="loading" min-height="180px" :rows="4">Loading invoice ledger…</app-block-loader>
      <div v-else-if="error" class="booking-finance__error" role="alert">
        <span>{{ error }}</span>
        <app-button theme="dark" size="small" @click.native="loadLedger">Retry</app-button>
      </div>
      <p v-else-if="!entries.length" class="booking-finance__message">No invoice ledger entries yet.</p>
      <div v-else class="booking-finance__table-scroll">
        <table aria-labelledby="booking-invoice-ledger-title">
          <thead><tr><th scope="col">#</th><th scope="col">Event</th><th scope="col" class="booking-finance__amount">Amount</th><th scope="col">Description</th><th scope="col">Occured</th><th scope="col">Created</th></tr></thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td>{{ entry.id }}</td>
              <td class="booking-finance__event">{{ entry.event_type || '—' }}</td>
              <td class="booking-finance__amount">{{ amount(entry.amount) }}</td>
              <td class="booking-finance__description">{{ entry.description || '—' }}</td>
              <td><time v-if="dateTime(entry.occured_at)" :datetime="entry.occured_at">{{ dateTime(entry.occured_at) }}</time><span v-else>—</span></td>
              <td class="booking-finance__created">
                <div v-if="entry.created_by" class="booking-finance__creator">
                  <span class="booking-finance__avatar" aria-hidden="true">
                    <img v-if="creatorPhoto(entry.created_by) && failedPhotos[entry.id] !== creatorPhoto(entry.created_by)" :src="creatorPhoto(entry.created_by)" alt="" @error="$set(failedPhotos, entry.id, creatorPhoto(entry.created_by))" />
                    <span v-else>{{ creatorInitials(entry.created_by) }}</span>
                  </span>
                  <span>{{ creatorName(entry.created_by) }}</span>
                </div>
                <time v-if="dateTime(entry.created_at)" :datetime="entry.created_at">{{ dateTime(entry.created_at) }}</time><span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { getFlightsBookingInvoiceLedger } from '@/http'
import BookingPreviousPaymentModal from './BookingPreviousPaymentModal.vue'
import { dateTime, empty, responseError } from './booking-detail'

export default {
  name: 'FlightBookingFinance',
  components: { BookingPreviousPaymentModal },
  props: { booking: { type: Object, required: true }, passengersRefreshKey: { type: Number, default: 0 } },
  data() { return { entries: [], loading: false, error: '', requestId: 0, failedPhotos: {} } },
  computed: {
    bookingId() { return this.booking.id },
    paymentModalName() { return `booking-previous-payment-${this.bookingId}` }
  },
  watch: {
    bookingId: { immediate: true, handler() { this.loadLedger() } },
    passengersRefreshKey() { this.loadLedger() }
  },
  beforeDestroy() { this.requestId++ },
  methods: {
    dateTime,
    focusPaymentButton() { if (this.$refs.previousPayment) this.$refs.previousPayment.$el.focus() },
    amount(value) {
      if (empty(value) || !Number.isFinite(Number(value))) return '—'
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value))
    },
    creatorName(creator) { return (creator.full_name || [creator.first_name, creator.last_name].filter(Boolean).join(' ')).trim() || (creator.id ? `User #${creator.id}` : '—') },
    creatorPhoto(creator) { return (creator.crop && creator.crop.crop_url) || '' },
    creatorInitials(creator) {
      const names = this.creatorName(creator).split(/\s+/).filter(Boolean)
      return [names[0][0], names.length > 1 ? names[names.length - 1][0] : ''].join('').toUpperCase()
    },
    async loadLedger() {
      const requestId = ++this.requestId
      this.entries = []
      this.error = ''
      this.failedPhotos = {}
      this.loading = false
      if (!this.booking.id) return
      this.loading = true
      try {
        const { data } = await getFlightsBookingInvoiceLedger(this.booking.id, { localErrors: true })
        if (requestId !== this.requestId) return
        if (!Array.isArray(data)) throw new Error('Invalid invoice ledger response')
        this.entries = data
      } catch (error) {
        if (requestId === this.requestId) this.error = responseError(error, 'Unable to load invoice ledger')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-finance-actions { display: flex; justify-content: flex-start; margin-bottom: 16px; }
.booking-finance { min-width: 0; overflow: hidden; border: 1px solid #dce0d6; border-radius: 8px; background: #fff; }
.booking-finance__header { padding: 14px 18px; border-bottom: 1px solid #dce0d6; background: #fafafa; }
.booking-finance__header h2 { margin: 0; font-size: 18px; font-weight: 500; }
.booking-finance__table-scroll { overflow-x: auto; }
.booking-finance table { width: 100%; min-width: 920px; border-collapse: collapse; font-size: 13px; }
.booking-finance th, .booking-finance td { padding: 12px 16px; text-align: left; vertical-align: top; }
.booking-finance th { color: #62685d; background: #fafafa; font-weight: 500; white-space: nowrap; }
.booking-finance td { border-top: 1px solid #dce0d6; }
.booking-finance .booking-finance__amount { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.booking-finance__event { max-width: 180px; overflow-wrap: anywhere; }
.booking-finance__description { width: 28%; min-width: 180px; white-space: pre-wrap; overflow-wrap: anywhere; }
.booking-finance time { white-space: nowrap; }
.booking-finance__created { min-width: 180px; }
.booking-finance__creator { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.booking-finance__avatar { display: flex; align-items: center; justify-content: center; flex: 0 0 24px; width: 24px; height: 24px; overflow: hidden; border: 1px solid #dce0d6; border-radius: 50%; background: #f0f6e7; color: #527b12; font-size: 10px; }
.booking-finance__avatar img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; }
.booking-finance__message { margin: 0; padding: 18px; color: #62685d; font-size: 14px; }
.booking-finance__error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 18px; color: #b03932; }
</style>
